import { TControlledFormComponentProps } from '@chocolate-soup-inc/cs-frontend-components';
import _ from 'lodash';
import { FieldValues } from 'react-hook-form';
import { TCompanyFieldsFragment, TListOfficesQuery, TWorkAnniversaryGiftVariations } from '../../generated/graphql';
import { getEmployeeDependantFormFields } from '../dependant/formFields';

type TGetEmployeeFormFieldsProps = {
  addDependants?: boolean;
  company: TCompanyFieldsFragment;
  offices?: TListOfficesQuery['listOffices']['items'];
};

export const getEmployeeFormFields = <T extends FieldValues = FieldValues>(props: TGetEmployeeFormFieldsProps) => {
  const { addDependants = false, company, offices } = props;

  const formFields: TControlledFormComponentProps[] = [
    {
      type: 'section',
      title: "Employee's required information."
    },
    {
      label: 'Employee ID',
      name: 'externalId',
      type: 'textField'
    },
    {
      label: 'First Name',
      name: 'firstName',
      type: 'textField'
    },
    {
      label: 'Last Name',
      name: 'lastName',
      type: 'textField'
    }
  ];

  formFields.push({
    label: 'Hire Date',
    name: 'hireDate',
    type: 'datePicker',
    inputOptions: {
      className: 'datePickerField'
    }
  });

  if (company.employeeBirthdayActivated)
    formFields.push({
      label: 'Birth Date',
      name: 'birthDate',
      type: 'datePicker',
      inputOptions: {
        className: 'datePickerField'
      }
    });

  if (company.employeeNewHireKitActivated && company.hasCustomOnboardBrick)
    formFields.push({
      label: 'Custom Onboard Brick',
      name: 'customOnboardBrick',
      type: 'textField'
    });

  formFields.push({
    type: 'section',
    title: 'Optional Information'
  });

  if (offices && _.compact(offices).length > 0)
    formFields.push({
      className: 'col-span-1',
      type: 'select',
      name: 'officeId',
      label: 'Office',
      inputOptions: {
        options: [
          ..._.compact(offices).map((office) => ({
            label: office.name,
            value: office.id
          }))
        ]
      }
    });

  formFields.push(
    {
      name: 'preferredFirstName',
      label: 'Preferred First Name',
      type: 'textField'
    },
    {
      name: 'email',
      label: 'Email',
      type: 'textField'
    },
    {
      name: 'phoneNumber',
      label: 'Phone number',
      type: 'textField',
      inputOptions: {
        transformIn: (data: any) => {
          if (Array.isArray(data)) return data.join('\n');

          return data;
        },
        transformOut: (data: any) => {
          if (data == null) return [];
          else return data.split('\n');
        }
      }
    },
    {
      className: 'col-span-2',
      name: 'foodPreferences',
      label: 'Allergies & Dietary Restrictions',
      type: 'textArea'
    }
  );

  if (company.deliveryMethod !== 'office')
    formFields.push(
      {
        type: 'section',
        title: 'Delivery Address'
      },
      {
        className: 'col-span-2',
        type: 'textField',
        label: 'Address line 1',
        name: 'address.address1'
      },
      {
        className: 'col-span-2',
        type: 'textField',
        label: 'Address line 2',
        name: 'address.address2'
      },
      {
        type: 'textField',
        label: 'City',
        name: 'address.city'
      },
      {
        type: 'textField',
        label: 'State / Province',
        name: 'address.state'
      },
      {
        type: 'countrySelect',
        label: 'Country',
        name: 'address.country'
      },
      {
        type: 'textField',
        label: 'Zip Code',
        name: 'address.zipCode'
      }
    );

  if (addDependants)
    formFields.push(
      {
        type: 'section',
        title: 'Dependants'
      },
      {
        type: 'fieldArray',
        name: 'dependants',
        addLabel: 'Add Dependant',
        label: 'Dependant #{{counter}}',
        fields: getEmployeeDependantFormFields({ company })
      }
    );

  formFields.push(
    {
      type: 'section',
      title: 'SWAG Information'
    },
    {
      name: 'tShirtSize',
      label: 'T-Shirt Size',
      type: 'textField'
    },
    {
      name: 'sweaterSize',
      label: 'Sweater Size',
      type: 'textField'
    }
  );

  const canDonateAnniversaryGift = company.employeeWorkAnniversaryActivated && company.workAnniversaryGiftVariation === TWorkAnniversaryGiftVariations.Deluxe;
  if (company.employeeBirthdayActivated || canDonateAnniversaryGift) {
    formFields.push({
      type: 'section',
      title: 'Donation Information'
    });
    if (company.employeeBirthdayActivated)
      formFields.push({
        className: 'col-span-2',
        type: 'switch',
        label: 'Donate birthday gift to charity',
        name: 'donateBirthdayGift'
      });

    if (canDonateAnniversaryGift)
      formFields.push({
        className: 'col-span-2',
        type: 'switch',
        label: 'Donate work anniversary gift to charity',
        name: 'donateWorkAnniversaryGift'
      });
  }

  return formFields as TControlledFormComponentProps<T>[];
};
